import { useMutation } from '@tanstack/react-query';

// Get the backend URL
const getBackendUrl = () => {
  // Use environment variable if available, otherwise use localhost
  const url = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  // Ensure URL has proper scheme
  if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
    return `http://${url}`;
  }
  return url;
};

interface SendCodeResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    expiresIn: number;
  };
}

interface VerifyCodeResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    resetId: number;
    verified: boolean;
    loginId?: string; // LoginId for password reset
  };
}

interface MarkUsedResponse {
  success: boolean;
  message: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message: string;
  data?: {
    loginId: string;
  };
}

// Send password reset code
export async function sendPasswordResetCode(email: string): Promise<SendCodeResponse> {
  const response = await fetch(`${getBackendUrl()}/api/password-reset/send-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, source: 'ApexBet' }),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Failed to send reset code');
  }
  
  return data;
}

// Verify password reset code
export async function verifyResetCode(email: string, code: string): Promise<VerifyCodeResponse> {
  const response = await fetch(`${getBackendUrl()}/api/password-reset/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, code }),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Failed to verify reset code');
  }
  
  return data;
}

// Mark reset code as used
export async function markResetCodeUsed(resetId: number): Promise<MarkUsedResponse> {
  const response = await fetch(`${getBackendUrl()}/api/password-reset/mark-used`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ resetId }),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Failed to mark code as used');
  }
  
  return data;
}

// Reset password with verified reset ID
export async function resetPassword(resetId: string, email: string, newPassword: string): Promise<ResetPasswordResponse> {
  const response = await fetch(`${getBackendUrl()}/api/password-reset/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ resetId, email, newPassword }),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Failed to reset password');
  }
  
  return data;
}

// TanStack Query hooks
export function useSendResetCode() {
  return useMutation({
    mutationFn: (email: string) => sendPasswordResetCode(email),
    onSuccess: (data) => {
      console.log('Reset code sent:', data);
    },
    onError: (error) => {
      console.error('Failed to send reset code:', error);
    },
  });
}

export function useVerifyResetCode() {
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) => verifyResetCode(email, code),
    onSuccess: (data) => {
      console.log('Reset code verified:', data);
    },
    onError: (error) => {
      console.error('Failed to verify reset code:', error);
    },
  });
}

export function useMarkResetCodeUsed() {
  return useMutation({
    mutationFn: (resetId: number) => markResetCodeUsed(resetId),
    onSuccess: (data) => {
      console.log('Reset code marked as used:', data);
    },
    onError: (error) => {
      console.error('Failed to mark code as used:', error);
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ resetId, email, newPassword }: { resetId: string; email: string; newPassword: string }) => 
      resetPassword(resetId, email, newPassword),
    onSuccess: (data) => {
      console.log('Password reset successful:', data);
    },
    onError: (error) => {
      console.error('Failed to reset password:', error);
    },
  });
}

// Link loginId to verified email (called after successful signup)
export async function linkLoginIdToEmail(email: string, loginId: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${getBackendUrl()}/api/email/link-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, loginId }),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'Failed to link loginId');
  }
  
  return data;
}

export function useLinkLoginId() {
  return useMutation({
    mutationFn: ({ email, loginId }: { email: string; loginId: string }) => linkLoginIdToEmail(email, loginId),
    onSuccess: (data) => {
      console.log('LoginId linked to email:', data);
    },
    onError: (error) => {
      console.error('Failed to link loginId:', error);
    },
  });
}

